import { Badge, Box, IconButton } from '@mui/material';
import { Magnify } from 'mdi-material-ui';
import { UIStatusGroup } from '../types';
import { Dispatch, SetStateAction, useRef } from 'react';
import { getFile } from 'src/services/attachment';
import { VigoAttachment } from 'src/@vigo/vigo-filedrop';
import { AttachmentDto } from 'src/models/attachment';
import { Products, useGetProductLevel } from 'src/@core/utils/getProductLevel';

interface StatusDocumentsProps {
  status: UIStatusGroup;
  setDocumentViewerOpen: Dispatch<SetStateAction<boolean>>;
}

/**
 * Retrieves the file blob with the given ID.
 *
 * @param {string} id - The ID of the file.
 * @return {Promise<any>} The file blob.
 */
const getFileBlob = async (id: string) => {
  const { data } = await getFile(id);

  return data;
};

const StatusDocuments = ({ status, setDocumentViewerOpen }: StatusDocumentsProps) => {
  const prod = useGetProductLevel();

  const imgRef0 = useRef<HTMLImageElement>(null);
  const imgRef1 = useRef<HTMLImageElement>(null);
  const refs = [imgRef0, imgRef1];

  const documents =
    prod === Products.MyPortal
      ? (status?.statusDocuments ?? []).filter(doc => !doc.isHidden)
      : status?.statusDocuments ?? [];

  return (
    <Box
      sx={{
        width: '20%',
        display: 'flex',
        justifyContent: 'center',
        borderLeft: '1px solid #eff1f9',
        position: 'relative'
      }}>
      {documents.length > 0 && (
        <Box
          sx={{
            alignSelf: 'center',
            width: '60px',
            height: '60px',
            backgroundColor: '#acacac',
            position: 'relative',
            '&:hover > div.documentOverlay': { display: 'flex' }
          }}>
          <Box sx={{ position: 'absolute', top: -8, right: 0, zIndex: status.statusDocuments.length + 1 }}>
            <Badge badgeContent={documents?.length ?? 0} color="primary" />
          </Box>

          {/* Only handling images for now */}
          {documents.map((image, index) => {
            if (index > 1) return;

            /**
             * File is of type TIFF
             */
            if ((image as VigoAttachment)?.type == 'image/tiff' || (image as AttachmentDto)?.fileType == 'image/tiff') {
              return (
                <Box
                  key={image.id}
                  sx={{
                    overflow: 'hidden',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    objectFit: 'cover',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    zIndex: status.statusDocuments.length - index,
                    transform: `translate(${10 * index}px, -${10 * index}px)`
                  }}>
                  <img
                    src="/images/pages/epod_tiff.png"
                    alt="epod tiff placeholder"
                    style={{
                      width: '100%',
                      marginBlock: 'auto',
                      display: 'inline-block',
                      objectFit: 'cover',
                      transform: 'scale(2)'
                    }}
                  />
                </Box>
              );
            }

            if (!image.thumbnail) {
              // If no image thumbnail property we need to get the blob and build an image from that.
              getFileBlob(image.id ?? '').then(blob => {
                if (!refs[index].current) return;

                const fileReader = new FileReader();
                fileReader.readAsDataURL(blob as unknown as Blob);
                fileReader.onload = function (e) {
                  if (!e?.target) return;

                  const image = new Image();
                  image.src = e.target.result as string;
                  image.onload = function () {
                    refs[index].current!.src = image.src;
                  };
                };
              });

              return (
                <img
                  key={image.id}
                  ref={refs[index]}
                  alt="status document"
                  width="100%"
                  height="100%"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    zIndex: status.statusDocuments.length - index,
                    transform: `translate(${10 * index}px, -${10 * index}px)`,
                    ...(index === 0 &&
                      status.statusDocuments.length > 1 && {
                        boxShadow: '4px -4px 0px 0px #fff'
                      })
                  }}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null;
                    currentTarget.remove();
                  }}
                />
              );
            }

            return (
              // If there is an image thumbnail property we can use it to build the base64 image
              <img
                key={image.id}
                src={`data:${image.fileType};base64,${image.thumbnail}`}
                alt="status document"
                width="100%"
                height="100%"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  zIndex: status.statusDocuments.length - index,
                  transform: `translate(${10 * index}px, -${10 * index}px)`,
                  ...(index === 0 &&
                    status.statusDocuments.length > 1 && {
                      boxShadow: '4px -4px 0px 0px #fff'
                    })
                }}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null;
                  currentTarget.remove();
                }}
              />
            );
          })}

          <Box
            className="documentOverlay"
            sx={{
              display: 'none',
              position: 'absolute',
              top: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0,0,0, 0.6)',
              justifyContent: 'flex-end',
              alignItems: 'flex-end',
              zIndex: 99
            }}>
            <IconButton onClick={() => setDocumentViewerOpen(true)}>
              <Magnify htmlColor="#fff" />
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default StatusDocuments;
